import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ConfigurationModel } from './models/configuration';
import { Project } from './models/project';
import { UserSettings, UserSettingsDTO } from './models/UserSettings';
import { AreaService } from './services/area.service';
import { CalculationsService } from './services/calculations.service';
import { ConfigurationColorService } from './services/configuration-color-service';
import { ConfigurationService } from './services/configuration.service';
import { CoordinatePointsFilterService } from './services/coordinate-points-filter.service';
import { EndpointsService } from './services/endpoints.service';
import { MapService } from './services/map.service';
import { ModalControlService } from './services/modal-control.service';
import { NavaidsFilterService } from './services/navaids-filter.service';
import { ObstacleDataService } from './services/obstacle-data.service';
import { PointsService } from './services/points.service';
import { ProjectService } from './services/project.service';
import { RouteSegmentsService } from './services/route-segments.service';
import { UiBlockService } from './services/ui-block.service';
import { UserSettingsService } from './services/userSettings.service';
import { ProgressService } from './services/progress.service';
import { SearchService } from './services/search.service';
import { ContextualHelpService } from './services/contextualHelpService';
import { SaamTrafficService } from './services/saamtrafficservice';
import { firstValueFrom } from 'rxjs';
import { MenuComponent } from './menu/menu.component';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { Router, ActivatedRoute } from '@angular/router';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, IdTokenClaims, InteractionRequiredAuthError, InteractionStatus, InteractionType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { DocSectorizationDisplayService } from './services/doc-sectorization-display.service';
import { ShortcutsModalControlService } from './popup/configurations/services/shortcuts-modal-control.service';
import { AirspaceBoundariesControlService } from './services/airspace-boundaries-control.service';
import { NavaidsService } from './services/navaids.service';
import { TabsService } from './services/tabsService';
import { MenuManagementService } from './menu/services/menu-management.service';
import { environment } from 'src/environments/environment';
import { KeycloakService } from './services/keycloak.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string,
  tfp?: string,
};

//#region GRIT
import {GritProjectService} from "./services/grit/grit.project.service";
import {lastValueFrom} from "rxjs";
//endregion

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  @BlockUI() blockUI: NgBlockUI;
  title = 'Demeter';
  expirationTokenMessage: boolean = false;

  username: string | null = null;

  authenticated = false;

  loginInProgress=false;

  constructor(private uiBlock: UiBlockService,
    private projectController: ProjectService,
    private configurationService: ConfigurationService,
    private pointsService: PointsService,
    private areaService: AreaService,
    private routeSegmentService: RouteSegmentsService,
    private obstacleDataService: ObstacleDataService,
    private calculationService: CalculationsService,
    private userSettingsService: UserSettingsService,
    private http: HttpClient,
    private endpointService: EndpointsService,
    private configurationColor: ConfigurationColorService,
    private mapService: MapService,
    private coordinatePointsFilterService: CoordinatePointsFilterService,
    private navaidsFilterService: NavaidsFilterService,
    private modalService: ModalControlService,
    private progressService: ProgressService,
    public searchService: SearchService,
    public contextualHelpService: ContextualHelpService,
    public saamtrafficService: SaamTrafficService,
    //#region GRIT
    private gritProjectService: GritProjectService,
    //endregion
    public menuService: MenuManagementService,
    private readonly authService: MsalService,
    private readonly router: Router,
    private readonly docSectorizationDisplayService: DocSectorizationDisplayService,
    private readonly shortcutsModalControlService: ShortcutsModalControlService,
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly cookieService: CookieService,
    private readonly keycloakService: KeycloakService,
    private readonly route: ActivatedRoute
    ) { }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
  if (this.loginInProgress) {
    return;
  }
  this.loginInProgress = true;

  if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
          this.loginInProgress = false;
        }, () => { this.loginInProgress = false; });
    } else {
      this.authService.loginPopup(userFlowRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
          this.loginInProgress = false;
        }, () => { this.loginInProgress = false; });
    }
  } else {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect(userFlowRequest);
    }
    // For redirect, set flag back to false after navigation
    //this.loginInProgress = false;
  }
}

  async checkAndSetActiveAccountOnB2C() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      // add your code for handling multiple accounts here
      activeAccount = this.authService.instance.getAllAccounts()[0];
      this.authService.instance.setActiveAccount(activeAccount);
    }

    if (activeAccount) {
      let idToken = null;
      let newLogin = false;

      let currentToken: string;
      if (environment.useB2cAuthentication) {
        currentToken = sessionStorage.getItem(EndpointsService.AUTH_TOKEN);
      } else if (environment.useKeycloakAuthentication) {
        currentToken = this.keycloakService.getToken();
      } else {
        currentToken = this.cookieService.get(EndpointsService.AUTH_TOKEN);
      }
      // console.log("Current token on checkAndSetActiveAccount:", currentToken);
      if (EndpointsService.isValidJwt(currentToken) && !AppComponent.isTokenExpired(currentToken)) {
        idToken = currentToken;
      } else {
        // new login
        newLogin = true
        sessionStorage.removeItem(EndpointsService.SESSION_ID);  // if new login, remove old session id
        if (activeAccount.idToken && !this.isForgotPasswordToken(activeAccount.idToken)) {
          idToken = activeAccount.idToken;
        }
      }
      if (!idToken) {
        const authenticationResult: AuthenticationResult = await this.authService.instance.acquireTokenSilent({
          scopes: [],
          account: activeAccount
        })
        idToken = authenticationResult.idToken;
      }

       if (!this.isB2cGroupClaimInToken(idToken)) {
        newLogin = true
        sessionStorage.removeItem(EndpointsService.SESSION_ID);  // if new login, remove old session id
        this.authService.instance.logoutRedirect({
          postLogoutRedirectUri: '/permissionError.html', // Custom URL
        });
        this.authService.instance.setActiveAccount(null);
        return;
      }

      await this.endpointService.initializeEndpointsService(idToken, newLogin);

      this.authenticated = idToken != null;
    }
  }

  async checkAndSetActiveAccountOnKeycloak() {
    let idToken = null;
    let newLogin = false;

    let currentToken: string;
    try {
      await this.keycloakService.updateToken();
      currentToken = this.keycloakService.getToken();
      // console.log("Current token on checkAndSetActiveAccount (after updateToken):", currentToken);
      if (EndpointsService.isValidJwt(currentToken) && !AppComponent.isTokenExpired(currentToken)) {
        idToken = currentToken;
      } else {
        // new login
        newLogin = true;
        sessionStorage.removeItem(EndpointsService.SESSION_ID);  // if new login, remove old session id
      }
    } catch (e) {
      // Token refresh failed, log out
      console.error('Keycloak silent token refresh failed', e);
      this.keycloakService.logout(false);
      return;
    }

    await this.endpointService.initializeEndpointsService(idToken, newLogin);
    this.authenticated = idToken != null;
  }

  redirectToPermissionError() {
    window.location.href = '/permissionError.html';
  }

  isForgotPasswordToken(token: string): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1])); // Decode the JWT payload
      return payload?.isForgotPassword === true;
    } catch (error) {
      console.error("Invalid token", error);
      return false;
    }
  }

  isB2cGroupClaimInToken(token: string): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1])); // Decode the JWT payload
      return payload?.extension_USRGROUP == environment.b2cGroupClaimValue;
    } catch (error) {
      console.error("Invalid token", error);
      return false;
    }
  }


  private checkAuthenticationLoop(): void {
    const interval = setInterval(() => {
      if (this.authenticated) {
        clearInterval(interval); // Stop checking once authenticated
      } else if (!this.loginInProgress) {
        this.login();
      }
    }, 1000); // Check every second
  }


  ngOnInit() {
    this.initApplication();
  }

  private async initApplication() {

    if (environment.useB2cAuthentication) {
      try {
        await this.authService.instance.handleRedirectPromise();
        await this.checkAndSetActiveAccountOnB2C();
      } catch (e) {
        console.error("Error setting B2C authentication status", e);
      }
      this.checkAuthenticationLoop();
    } else if (environment.useKeycloakAuthentication) {
      try {
        // await this.authService.instance.handleRedirectPromise();
        await this.checkAndSetActiveAccountOnKeycloak();
      } catch (e) {
        console.error("Error setting Keycloak authentication status", e);
      }
      this.checkAuthenticationLoop();
    } else {
      this.endpointService.initHttpOptions();
      const authToken = this.endpointService.getAuthorizationToken();
      this.authenticated = !!authToken && authToken !== 'null' && authToken !== 'undefined';

    }

     if (!this.authenticated) {
      document.cookie = "AuthToken" + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';//NEW
      window.location.href = '/login.html';//NEW

      return;
    }
    //this.checkIfSessionIsValidLoop();

    this.checkIfTokenIsToExpire();
    this.uiBlock.blockUI = this.blockUI;

    await this.mapService.initMapUI();

    this.shortcutsModalControlService.updateShortcutsList();
    this.navaidsFilterService.initIcaoCodes();


    this.configurationService.deleteTerrainUploadInProgress();
    let projects : Project[] = await lastValueFrom(this.projectController.getProjects());
    this.projectController.activeProject = projects.find(x => x.active);
    let usersettings :UserSettingsDTO = await this.http.get<UserSettingsDTO>(this.endpointService.endpointUserSettingsGet+'/'+this.projectController.activeProject.id,
    this.endpointService.httpOptions).toPromise();

    this.projectController.activeProject.userSettingsJSON = usersettings.usersettingsstr;
    this.userSettingsService.updateUserSettingsFields(JSON.parse(this.projectController.activeProject.userSettingsJSON));

    this.pointsService.initializeVisibilities(this.userSettingsService.userSettings);
    this.configurationService.getAllConfigurations(this.projectController.activeProject.id).subscribe((data) => {
      this.configurationService.selectedRow = -1;
      this.configurationService.configurationList = [];
      for (const element of data) {
        this.configurationService.configurationList.push(new ConfigurationModel(element));
        if (element.active) {
          this.uiBlock.startBlock("Adding entities to map...");
          this.configurationService.getConfiguration(element.id).subscribe((config) => {
            this.uiBlock.stopBlock();
            this.configurationService.currentConfiguration = ConfigurationModel.map(config);
            this.docSectorizationDisplayService.rcsInit();
            this.calculationService.initializeCalculationService(JSON.parse(this.configurationService.currentConfiguration.cumulativeColorSettingsJson));
            this.configurationColor.initialize();

            this.userSettingsService.userSettings.visibleStaticLayerIDs
              .forEach((layerID) => {
                let staticLayer = this.configurationService.currentConfiguration.layers
                  .find((layer) => layer.id == layerID);
                if (staticLayer) {
                  this.mapService.activateLayer(staticLayer);
                }
              })

            for (const staticLayer of this.configurationService.currentConfiguration.layers) {
              if (staticLayer.type != 'GeoJsonLayer' || this.userSettingsService.userSettings.visibleStaticLayerIDs.includes(staticLayer.id))
                continue;
              this.mapService.populateGeoJsonLayer(staticLayer);
            }

            this.saamtrafficService.fetchAndAddSaamTrafficLayers(true);
            this.configurationService.updateCoordenateControlRCSList();
            this.mapService.reprojectMap(this.configurationService.coordenateService.activeRCS.epsg);
            this.navaidsFilterService.filters = cloneDeep(this.userSettingsService.userSettings.navaidFilters);
            this.routeSegmentService.loadState(this.projectController.activeProject.id, true);
            this.obstacleDataService.loadState(this.projectController.activeProject.id);
            this.coordinatePointsFilterService.filters = cloneDeep(this.userSettingsService.userSettings.coordinatePointFilters);
            this.pointsService.fetchAndAddUserLayersToMap(true);
            this.configurationService.fetchTerrainsByProject(this.projectController.activeProject.id);

            // -----------------------------------------------------------------------------
            // Implementation with the COMPLETE results of Navaids based on the visible area of the map.
            // -----------------------------------------------------------------------------
            // this.areaService.fetchAllAreasAndSetDefault();
            // this.pointsService.fetchAndAddNavaids(true);
            // this.pointsService.fetchAndAddCoordinatePoints(true);
            // this.pointsService.fetchAndAddAirSpaceBoundaries(true);
            // this.pointsService.fetchAndAddProcedures();
            //this.pointsService.fetchAndAddTrajectories(true);

            // -----------------------------------------------------------------------------
            // Implementation with the PARTIAL results of Entities based on the visible area of the map.
            // -----------------------------------------------------------------------------
            this.areaService.fetchAllAreasAndSetDefault(true, () => {
              const map = this.mapService.getMap('map');
              const loadEntities = (isFirstCall: boolean = false) => {
                const extent = this.mapService.getCurrentExtent();
                this.progressService.initializeProgressService(extent);
                this.pointsService.fetchAndAddPartialNavaids(extent, isFirstCall);
                this.pointsService.fetchAndAddPartialCoordinatePoints(extent, isFirstCall);
                this.pointsService.fetchAndAddPartialProcedures(extent, isFirstCall);
                this.pointsService.fetchAndAddPartialAirSpaceBoundaries(extent, isFirstCall);
                this.pointsService.fetchAndAddPartialTrajectories(extent, isFirstCall);
              };

            loadEntities(true);
            map.on('moveend', () => loadEntities(false));
          });
            this.pointsService.fetchAllAnnotations(true);
            this.pointsService.fetchAndAddFlightInspection(true);
            this.pointsService.fetchAndAddProcessingTool(true);

            //#region GRIT
            this.gritProjectService.switchProject(this.projectController.activeProject);
            //endregion

          },(error) => {
            console.log(error);
          });
        }
      }
    },
      (error) => {
        console.log(error);
      });
    this.projectController.projects = projects;
    if (this.projectController.selectedProject == undefined) {
      this.projectController.selectProject(this.projectController.activeProject);
      this.projectController.selectedProject = this.projectController.activeProject;
    } else {
      this.projectController.selectProject(this.projectController.selectedProject);
    }
    this.openSupportTicketFromUrl();

  }


  private async checkIfSessionIsValid(): Promise<boolean> {
    try {
      const fetchedSessionId = await firstValueFrom(this.http.get(this.endpointService.fetchSessionId, {
        headers: this.endpointService.httpOptions.headers,
        responseType: "text"
      }))
      if (fetchedSessionId == null || this.endpointService.getSessionID() != fetchedSessionId) {
        this.modalService.showMesssageModal
          (
            "Multiple Tabs",
            "You have multiple GRIT tabs open, you will be logged out. Please proceed and login again or close this tab.",
            null,
            () => { environment.useB2cAuthentication || environment.useKeycloakAuthentication ? this.menuService.logout() : MenuComponent.oldLogout() }
          );
        return false;
      }
    } catch (e) {
      console.error(e);
    }
    return true;
  }

  private checkIfSessionIsValidLoop() {
    if (this.endpointService.isLocalHost()) {
      return;
    }

    let messageDisplayed: boolean = false;

    setInterval(async () => {
      if (!messageDisplayed) {
        if (!(await this.checkIfSessionIsValid())) {
          messageDisplayed = true;
        }
      }

    }, 5000);
  }


  private checkIfTokenIsToExpire() {
    let auxthis = this;
    setInterval(function () {
      const currentToken = auxthis.endpointService.getAuthorizationToken();
      if (currentToken && AppComponent.isTokenExpired(currentToken)) {
        console.log('Token expired, logging out...');
        if (environment.useB2cAuthentication || environment.useKeycloakAuthentication) {
          auxthis.menuService.logout();
        } else {
          document.cookie = "AuthToken" + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
          MenuComponent.oldLogout();
        }
      } else if (auxthis.isTokenAboutToExpired()) {
        if (!auxthis.expirationTokenMessage) {
          auxthis.modalService.showMesssageModal("Authentication Token", "The Authentication token will expire in less then 10 minutes. Please Reauthenticate in the aplication.");
        }
        auxthis.expirationTokenMessage = true;
      } else {
        auxthis.expirationTokenMessage = false;
        //console.log("Token is valid");
      }
    }, 1000);
  }

  private static getTokenExpirationTimeStamp(token: string): number {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    const { exp } = JSON.parse(jsonPayload);

    return exp;
  }
  private isTokenAboutToExpired(): boolean {
    let exp = AppComponent.getTokenExpirationTimeStamp(this.endpointService.getAuthorizationToken());
    const tenMinutesToexpire = this.timeDifference(new Date(exp * 1000), new Date(Date.now())) <= 10;
    return tenMinutesToexpire;
  }

  public static isTokenExpired(token: string): boolean {
    let exp = AppComponent.getTokenExpirationTimeStamp(token);
    const expired = Date.now() >= exp * 1000;
    return expired;
  }
  timeDifference(date1, date2) {
    var diff = Math.abs(date1 - date2);
    var minutes = Math.floor((diff / 1000) / 60);

    //console.log(minutes);
    return minutes;
  }


  private openSupportTicketFromUrl(): void {

    this.route.queryParams.subscribe(params => {

        const ticketId = Number(params['supportTicketId']);

        if (!ticketId) {
            return;
        }

        this.modalService.openSupportTickets(ticketId);

    });

}
}
